import { Box } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import WorkFlowTab from '@/components/WorkFlowTab'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import { CanvasAlignment } from '@/components/canvas/constants'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useAppSelector } from '@/hooks/redux'
import { useDualCanvasSize } from '@/hooks/useDualCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import KonvaCrown from '@/pages/stainingStudio/crown/KonvaCrown'
import { CROWN_BUTTON_WITH_COLOR } from '@/pages/stainingStudio/utils/constants'
import {
  useGenerateCrownRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import { PLACEMENT } from '@/store/appSlice'
import {
  removeStainingCorrectionKey,
  setSelectedStainingCorrectionKey,
  setStainingCorrectionRecipe,
} from '@/store/pfmCrown'
import {
  ImageCalibrationStatus,
  RecipeGenerationStatus,
  setImageCalibrationStatus,
  setRecipeGenerationStatus,
} from '@/store/sessionSlice'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  getProtocolValue,
  hasNonEmptyOutputDualCanvas,
  isImageCalibrated,
  saveRecipeGeneric,
} from '@/utils/helper'

import StainingCorrectionPanel from './panel/StainingCorrectionPanel'

const StainingCorrection = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'stainingCorrection.panel',
  })
  const { t: translation } = useTranslation()
  const alignment = CanvasAlignment.VERTICAL
  const dispatch = useDispatch()
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    'Base Color',
  )

  const chipsContainerRef = useRef<HTMLDivElement>(null)

  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [tooltipMessage, setTooltipMessage] = useState('')
  const [circlePos, setCirclePos] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [circleColor, setCircleColor] = useState<string>('#ffffff')
  const [activeCanvas, setActiveCanvas] = useState<PLACEMENT | null>(null)

  const [generateCrownRecipe] = useGenerateCrownRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const selectedColor = useAppSelector((store) => store.app.selectedColor)
  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )
  const selectedStainingCorrectionKey = useAppSelector(
    (state) => state.pfmCrown.selectedStainingCorrectionKey,
  )
  const stainingCorrectionRecipe = useAppSelector(
    (state) => state.pfmCrown.stainingCorrectionRecipe,
  )
  const selectedStainingCorrectionBrand = useAppSelector(
    (state) => state.pfmCrown.selectedStainingCorrectionBrand,
  )

  const handleDeleteStainingCorrection = useCallback(
    (crownKey: string) => {
      if (!dualCanvasImage) return

      dispatch(setSelectedStainingCorrectionKey(null))
      dispatch(removeStainingCorrectionKey({ key: crownKey }))
    },
    [dispatch, dualCanvasImage],
  )

  const [leftElements, rightElements] = useMemo(() => {
    if (!stainingCorrectionRecipe || !selectedCategory) return [[], []]

    const allowedKeys = Object.keys(
      CROWN_BUTTON_WITH_COLOR[
        selectedCategory as keyof typeof CROWN_BUTTON_WITH_COLOR
      ] || {},
    )

    return [
      Object.entries(stainingCorrectionRecipe[PLACEMENT.LEFT] || {})
        .filter(([key]) => allowedKeys.includes(key))
        .map(([key, recipe]) => ({
          component: (
            <KonvaCrown
              key={key}
              input={recipe.input}
              output={recipe.output}
              onDelete={handleDeleteStainingCorrection}
              tooltipPlacement={PLACEMENT.LEFT}
            />
          ),
          id: recipe.input?.fullmonolithiceffect,
        })),

      Object.entries(stainingCorrectionRecipe[PLACEMENT.RIGHT] || {})
        .filter(([key]) => allowedKeys.includes(key))
        .map(([key, recipe]) => ({
          component: (
            <KonvaCrown
              key={key}
              input={recipe.input}
              output={recipe.output}
              onDelete={handleDeleteStainingCorrection}
              tooltipPlacement={PLACEMENT.RIGHT}
            />
          ),
          id: recipe.input?.fullmonolithiceffect,
        })),
    ]
  }, [stainingCorrectionRecipe, selectedCategory])

  const { containerRef, dimensions } = useDualCanvasSize(
    alignment,
    chipsContainerRef?.current?.clientHeight,
  )

  const togglePlacement = (
    currentPlacement: string,
  ): PLACEMENT.LEFT | PLACEMENT.RIGHT => {
    return currentPlacement === PLACEMENT.LEFT
      ? PLACEMENT.RIGHT
      : PLACEMENT.LEFT
  }

  const placement = useAppSelector((state) => state.app.placement)
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const handleCalibration = (imageId: string, type: string) => {
    if (
      imageCalibrationStatus?.[imageId] ===
        ImageCalibrationStatus.CALIBRATING &&
      type === imageType.RAW
    ) {
      dispatch(
        setImageCalibrationStatus({
          id: imageId,
          status: ImageCalibrationStatus.CALIBRATED,
        }),
      )
    }
  }

  const handleFirstStepRecipeGeneration = (
    placement: PLACEMENT,
    toggledPlacement: PLACEMENT,
    type: string,
    imageId: string,
    _elementPos?: { x: number; y: number },
  ) => {
    if (!selectedStainingCorrectionKey) return

    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATE))
    const payload = {
      backgroundcolor: [],
      fullmonolithiceffect: selectedStainingCorrectionKey,
      protocol: getProtocolValue(dualCanvasImage?.[placement]),
      stainingbrand: selectedStainingCorrectionBrand || '',
      targetcolor: [
        selectedColor.L,
        selectedColor.a,
        selectedColor.b,
      ] as string[],
      crownPlacement: toggledPlacement,
    }

    setTooltipOpen(true)
    setTooltipMessage(t('secondStep'))

    dispatch(
      setStainingCorrectionRecipe({
        placement,
        key: selectedStainingCorrectionKey,
        data: {
          input: { ...payload, x: _elementPos?.x, y: _elementPos?.y },
          output: {},
        },
      }),
    )
  }

  const handleSecondStepRecipeGeneration = (
    placement: PLACEMENT,
    toggledPlacement: PLACEMENT,
    type: string,
    imageId: string,
    otherSideRecipe: any,
    _elementPos?: { x: number; y: number },
  ) => {
    if (!selectedStainingCorrectionKey) return

    dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATING))
    const payload = {
      backgroundcolor: [selectedColor.L, selectedColor.a, selectedColor.b] as
        | string
        | number
        | null[],
      fullmonolithiceffect: selectedStainingCorrectionKey,
      protocol: getProtocolValue(dualCanvasImage?.[placement]),
      stainingbrand: selectedStainingCorrectionBrand || '',
      targetcolor: otherSideRecipe?.input?.targetcolor,
    }
    toast.info(translation('recipeGenerate.inProgress'))
    generateCrownRecipe({ caseId, data: payload })
      .unwrap()
      .then((res) => {
        setTooltipOpen(false)
        dispatch(
          setStainingCorrectionRecipe({
            placement,
            key: selectedStainingCorrectionKey,
            data: {
              input: {
                ...{
                  ...payload,
                  targetcolor: [],
                },
                x: _elementPos?.x,
                y: _elementPos?.y,
              },
              output: res,
            },
          }),
        )
        dispatch(setRecipeGenerationStatus(RecipeGenerationStatus.GENERATED))
        dispatch(setSelectedStainingCorrectionKey(null))
        setTooltipOpen(false)
        toast.success(t('recipeGenerated'))
      })
      .catch((error) => {
        toast.error(error?.data?.message || t('recipeGenerationFailed'))
        console.error('Error generating crown recipe:', error)
      })
  }

  const handleRecipeGeneration = (
    placement: PLACEMENT,
    type: string,
    imageId: string,
    _elementPos?: { x: number; y: number },
  ) => {
    if (!selectedStainingCorrectionBrand) {
      toast.error(t('selectValidStainingBrand'))
      return
    }

    if (!selectedStainingCorrectionKey) return

    const toggledPlacement = togglePlacement(placement)
    const currentSideRecipe =
      stainingCorrectionRecipe?.[
        placement as keyof typeof stainingCorrectionRecipe
      ]?.[selectedStainingCorrectionKey]
    const otherSideRecipe =
      stainingCorrectionRecipe?.[
        toggledPlacement as keyof typeof stainingCorrectionRecipe
      ]?.[selectedStainingCorrectionKey]

    const isFirstStep =
      isEmpty((currentSideRecipe?.input as any)?.targetcolor) &&
      isEmpty((otherSideRecipe?.input as any)?.targetcolor)

    const isSecondStep = isEmpty((currentSideRecipe?.input as any)?.targetcolor)

    if (isFirstStep) {
      handleFirstStepRecipeGeneration(
        placement,
        toggledPlacement,
        type,
        imageId,
        _elementPos,
      )
    } else if (isSecondStep) {
      handleSecondStepRecipeGeneration(
        placement,
        toggledPlacement,
        type,
        imageId,
        otherSideRecipe,
        _elementPos,
      )
    }
  }

  const onImageClick = (
    type: string,
    placement: PLACEMENT,
    _elementPos?: { x: number; y: number },
  ) => {
    if (placement === PLACEMENT.CENTER) return

    const imageId = dualCanvasImage?.[placement]?.id

    if (imageId) {
      handleCalibration(String(imageId), type)
    }

    if (!isBothImageCalibrated || !imageId) {
      return
    }

    handleRecipeGeneration(placement, type, String(imageId), _elementPos)
  }

  const isBothSideImageSelected =
    !!dualCanvasImage?.[PLACEMENT.LEFT]?.id &&
    !!dualCanvasImage?.[PLACEMENT.RIGHT]?.id

  const isLeftImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.LEFT],
  )

  const isRightImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.RIGHT],
  )

  const isBothImageCalibrated = isLeftImageCalibrated && isRightImageCalibrated

  const onSaveAsImage = () => {
    generateImage(
      combinedRef as React.RefObject<HTMLDivElement>,
      'Staining Correction-Recipe.png',
    )
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      dualCanvasImage,
      recipeName,
      output: stainingCorrectionRecipe,
      workflow: Workflows?.PFM_CROWN,
      mutationFn: saveRecipe,
      recipeType: 'staining_fullmonolithic',
      closeDialog: () => setSaveRecipedialogOpen(false),
      inputType: 'dual',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      dualCanvasImage,
      recipeName,
      output: stainingCorrectionRecipe,
      workflow: Workflows?.PFM_CROWN,
      mutationFn: savePdf,
      recipeType: 'staining_fullmonolithic',
      inputType: 'dual',
      isPdf: true,
    })
  }
  return (
    <CaseInfoLayout
      disabled={hasNonEmptyOutputDualCanvas(stainingCorrectionRecipe)}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <WorkFlowTab width={dimensions.width * 2} />

        <Box display="flex" flexGrow={1} ref={containerRef} position="relative">
          {!imageEdit ? (
            <>
              <Box ref={combinedRef} display="flex">
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.LEFT]) ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.LEFT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick}
                  placement={PLACEMENT.LEFT}
                  type={dualCanvasImage?.[PLACEMENT.LEFT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.LEFT]?.id}
                  combinedRef={combinedRef}
                  konvaElements={leftElements}
                  rawWidth={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_height}
                  isActiveCircle={!!selectedStainingCorrectionKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
                <NewKonvaCanvas
                  width={dimensions?.width}
                  height={dimensions?.height}
                  imageUrl={
                    getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.RIGHT]) ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.latest_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.calibrated_image ||
                    dualCanvasImage?.[PLACEMENT.RIGHT]?.image
                  }
                  alignment={alignment}
                  onImageClick={onImageClick}
                  placement={PLACEMENT.RIGHT}
                  type={dualCanvasImage?.[PLACEMENT.RIGHT]?.type}
                  imageId={dualCanvasImage?.[PLACEMENT.RIGHT]?.id}
                  combinedRef={combinedRef}
                  konvaElements={rightElements}
                  rawWidth={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_width}
                  rawHeight={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_height}
                  isActiveCircle={!!selectedStainingCorrectionKey}
                  circlePos={circlePos}
                  setCirclePos={setCirclePos}
                  circleColor={circleColor}
                  setCircleColor={setCircleColor}
                  activeCanvas={activeCanvas}
                  setActiveCanvas={setActiveCanvas}
                />
              </Box>
              {isBothSideImageSelected && isBothImageCalibrated && (
                <StainingCorrectionPanel
                  setTooltipOpen={setTooltipOpen}
                  setTooltipMessage={setTooltipMessage}
                  dimensions={dimensions}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                />
              )}
              {containerRef.current && (
                <SnackbarTooltip
                  containerRef={containerRef}
                  message={tooltipMessage}
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                  placement="top"
                  offset={[0, -100]}
                  autoHideDuration={10000}
                  maxWidth={500}
                />
              )}
            </>
          ) : (
            <ImageEdit
              selectedImage={
                placement === PLACEMENT.LEFT
                  ? dualCanvasImage[PLACEMENT.LEFT]
                  : dualCanvasImage[PLACEMENT.RIGHT]
              }
            />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject as any}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default StainingCorrection
